
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TestTube, Laptop, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import EmbedScript from '@/components/EmbedScript';
import AccessibilityWidget from '@/components/AccessibilityWidget';
import MainNav from '@/components/MainNav';
import { useAuth } from '@/providers/AuthProvider';
import { ManageInstallations } from '@/components/installation/ManageInstallations';
import Footer from '@/components/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import VoiceAssistantConfig from '@/components/VoiceAssistantConfig';

const InstallationPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('install');
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <AccessibilityWidget />
      <header>
        <MainNav currentPath="installation" />
      </header>
      <main className="container py-8">
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
            </Link>
          </Button>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
                Installation Guide
              </h1>
              <p className="text-muted-foreground text-base md:text-lg">
                Everything you need to add the widget to your website.
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/installation/test" className="flex items-center gap-1">
                  <TestTube className="h-4 w-4" /> Test Installation
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Show installations management for logged-in users */}
        {user && (
          <div className="mb-8">
            <ManageInstallations />
          </div>
        )}
        
        <Tabs defaultValue="install" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="install" className="flex items-center gap-2">
              <Code className="h-4 w-4" /> Installation
            </TabsTrigger>
            <TabsTrigger value="voice" className="flex items-center gap-2">
              <Laptop className="h-4 w-4" /> Voice Configuration
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="install" className="mt-6">
            <div>
              <h2 className="text-xl md:text-2xl font-semibold mb-4">Installation Instructions</h2>
              <p className="text-muted-foreground mb-6">
                Follow these steps to add the accessibility widget to your website. Choose the standard installation
                for most websites, or the specialized Webflow installation if you're using Webflow.
              </p>
              <div className="bg-muted/30 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-medium mb-2">Platform-Specific Installation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-medium mb-2">Standard Websites</h4>
                    <p className="text-sm text-muted-foreground">
                      For most websites built with HTML, CSS and JavaScript, including React, Vue, Angular, and other frameworks.
                    </p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-medium mb-2">Webflow Sites</h4>
                    <p className="text-sm text-muted-foreground">
                      Specialized installation for Webflow sites that addresses Cloudflare security challenges.
                    </p>
                  </div>
                </div>
              </div>
              {/* Pass demo=true to show a sample script when no user is logged in */}
              <EmbedScript demo={!user} />
            </div>
          </TabsContent>
          
          <TabsContent value="voice" className="mt-6">
            <div>
              <h2 className="text-xl md:text-2xl font-semibold mb-4">Voice Assistant Configuration</h2>
              <p className="text-muted-foreground mb-6">
                Configure the voice commands and responses for your widget. Add custom routes, commands, and responses
                to provide a tailored experience for your users.
              </p>
              <VoiceAssistantConfig />
            </div>
          </TabsContent>
        </Tabs>
      </main>
      
      <Footer />
    </div>
  );
};

export default InstallationPage;
